import apiClient from "./client";

/**
 * Sync Logs API service
 */
export const syncLogsApi = {
  /**
   * Get paginated sync logs with optional filters
   */
  async getLogs({
    tenantId = null,
    providerType = null,
    isAutomatic = null,
    status = null,
    fromDate = null,
    toDate = null,
    page = 1,
    pageSize = 20,
  } = {}) {
    const params = new URLSearchParams();
    if (tenantId) params.append("tenantId", tenantId);
    if (providerType) params.append("providerType", providerType);
    if (isAutomatic !== null) params.append("isAutomatic", isAutomatic);
    if (status !== null) params.append("status", status);
    if (fromDate) params.append("fromDate", fromDate);
    if (toDate) params.append("toDate", toDate);
    params.append("page", page);
    params.append("pageSize", pageSize);

    return apiClient.get(`/synclogs?${params.toString()}`);
  },

  /**
   * Get a specific sync log by ID
   */
  async getLog(id) {
    return apiClient.get(`/synclogs/${id}`);
  },

  /**
   * Get sync statistics
   */
  async getStats({ tenantId = null, days = 30 } = {}) {
    const params = new URLSearchParams();
    if (tenantId) params.append("tenantId", tenantId);
    params.append("days", days);

    return apiClient.get(`/synclogs/stats?${params.toString()}`);
  },

  /**
   * Trigger a manual HR sync (full: locations + job roles + employees)
   */
  async triggerHrSync() {
    return apiClient.post("/integrations/sync/full-hr");
  },

  /**
   * Trigger a manual Till sync (TRONC records + clock records for a date range)
   * @param {string} [fromDate] - Start date (YYYY-MM-DD), defaults to last 7 days on server
   * @param {string} [toDate] - End date (YYYY-MM-DD), defaults to today on server
   */
  async triggerTillSync(fromDate, toDate) {
    const params = new URLSearchParams();
    if (fromDate) params.append("fromDate", fromDate);
    if (toDate) params.append("toDate", toDate);
    const query = params.toString();

    // Sync both clock records and TRONC records in parallel
    const [clockResult, troncResult] = await Promise.all([
      apiClient.post(`/integrations/sync/clock-records${query ? `?${query}` : ""}`),
      apiClient.post(`/integrations/sync/tronc-records${query ? `?${query}` : ""}`),
    ]);

    // Merge results - return combined response
    const clockData = clockResult?.data ?? {};
    const troncData = troncResult?.data ?? {};

    return {
      success: (clockResult?.success ?? false) || (troncResult?.success ?? false),
      data: {
        clockRecords: clockData,
        troncRecords: troncData,
        createdCount:
          (clockData.createdCount ?? clockData.recordsCreated ?? 0) +
          (troncData.createdCount ?? troncData.recordsCreated ?? 0),
        updatedCount:
          (clockData.updatedCount ?? clockData.recordsUpdated ?? 0) +
          (troncData.updatedCount ?? troncData.recordsUpdated ?? 0),
        skippedCount:
          (clockData.skippedCount ?? clockData.recordsSkipped ?? 0) +
          (troncData.skippedCount ?? troncData.recordsSkipped ?? 0),
      },
      message: "Clock records and TRONC records synced",
    };
  },
};

export default syncLogsApi;
